/*
Textes de la homepage v5 (design export "Homepage We Are Scene").
Les clés sont surchargées par l'API i18n si elles existent sous `home:v5.*`,
sinon on retombe sur ce dictionnaire local (fr / en / es).
*/
import getValue from 'get-value';
import { useTranslation } from '@/helpers/translate';

const fr = {
  hero: {
    title: 'Réservez des décors uniques',
    titleAccent: 'décors uniques',
    subtitle: "Plus d'1 million de scènes à réserver instantanément sur les lieux de milliers d'hôtes.",
    howItWorks: 'Comment ça marche ?',
    formats: ['Cinéma', 'Clip', 'Contenu social', 'Documentaire', 'Interview', 'Publicité', 'Shooting', 'TV & séries'],
    search: {
      location: 'localisation',
      dates: 'dates',
      keywords: 'mots-clés · IA',
      keywordPlaceholders: ["Îlot central, chambre d'enfant…", 'Verrière, brique, lumière du nord…', 'Piscine intérieure, marbre, années 70…', 'Rooftop, néon, vue dégagée…'],
      noOptions: 'Tapez un mot-clé…',
      button: 'Rechercher',
      calendar: {
        datesTitle: 'Vos dates',
        toHourly: 'Créneau horaire',
        arrival: 'Arrivée',
        departure: 'Départ',
        hoursShort: '{n} h',
        remove: 'Retirer cette date',
        flexibility: 'Flexibilité',
        flexFixed: 'Dates fixes',
        flexDays: '± {n} j',
        clear: 'Effacer',
        done: 'Terminé',
        more: '+{n}',
        period: {
          day: 'Journée', morning: 'Matin', afternoon: 'Après-midi', hour: 'Par heure',
        },
      },
    },
  },
  sceneLabel: 'Scène',
  proof: { count: '50 000+', label: 'productions' },
  selection: {
    kicker: 'sélection de la semaine',
    title: "Les lieux de milliers d'hôtes deviennent la scène de vos productions captivantes.",
    all: 'Tous les lieux',
    persons: 'pers.',
  },
  brief: {
    kicker: 'accompagnement expert',
    title: "Parlez-nous de votre production, notre équipe d'experts vous accompagne.",
    desc: 'Un interlocuteur dédié étudie votre brief, vous guide dans la recherche et peut vous proposer une sélection adaptée à votre projet et à votre budget.',
    cta: 'Confiez votre brief',
    imageAlt: "L'équipe We Are Scene vous accompagne",
    offTitle: "Quand le décor n'existe pas encore en ligne.",
    offDesc: 'Au-delà des lieux visibles, notre réseau de repéreurs accrédités apporte une réponse rapide et pertinente, sans surcoût, même pour les recherches les plus complexes.',
  },
  hof: {
    kicker: 'hall of fame',
    title: 'Plus vous tournez, et plus vous êtes aux devants de la scène.',
    desc: "La fidélité de votre compte vous récompense : les réservations de tous vos collaborateurs se cumulent, et les avantages profitent à toute l'équipe.",
    more: 'En savoir plus',
    tiers: {
      scout: { name: 'Scout', signature: 'Entrez en scène', threshold: 'Dès 5 réservations / an' },
      blockbuster: { name: 'Blockbuster', signature: 'Aux devants de la scène', threshold: 'Dès 10 réservations / an' },
      legend: { name: 'Legend', signature: 'Sous les projecteurs', threshold: 'Dès 30 réservations / an' },
    },
    plusPrefix: 'Legend +, ',
    plusUpTo: "jusqu'à",
    plusPrice: '49 €/mois avec engagement',
    plusSuffix: " : des avantages exclusifs allant jusqu'à -30%",
    plusCheck: 'Gagnant dès les premières réservations',
    plusFoot: 'Interlocuteur dédié · événements membres · suivi centralisé',
  },
  usages: {
    kicker: 'vos projets',
    titleLine1: 'Tous les formats audiovisuels.',
    titleLine2: 'Une seule plateforme,',
    titleFor: 'pour',
    words: ['votre film.', 'votre série.', 'votre pub.', 'votre clip.', 'votre shooting.', 'votre docu.'],
    prev: 'précédent',
    next: 'suivant',
    items: [
      { key: 'cinema', title: 'Cinéma', claim: 'Repérage premium, contrats en ligne.' },
      { key: 'clip', title: 'Clip', claim: 'Des décors à la hauteur de vos univers.' },
      { key: 'contenu', title: 'Contenu social', claim: 'Le décor le matin, le post le soir.' },
      { key: 'docu', title: 'Documentaire', claim: 'Des lieux vrais pour des histoires vraies.' },
      { key: 'interview', title: 'Interview', claim: 'Un cadre soigné, prêt à éclairer.' },
      { key: 'pub', title: 'Publicité', claim: 'Du brief au spot, sans friction.' },
      { key: 'shooting', title: 'Shooting', claim: 'Des décors prêts à shooter.' },
      { key: 'tv', title: 'TV & séries', claim: 'Du pilote à la saison 5.' },
    ],
  },
  pillars: {
    kicker: 'nos services',
    titleLine1: 'De la recherche à la production,',
    titleLine2: 'tout est couvert.',
    partner: 'en partenariat avec',
    items: [
      {
 key: 'ai', title: 'Matching IA', claim: 'Le bon décor, instantanément.', desc: "Reconnaissance d'image sur +1M de photos. Tapez quelques mots-clés, on remonte les décors qui matchent.",
},
      {
 key: 'cover', title: 'Scene Cover', claim: "Couverture jusqu'à 1,5 M€.", desc: 'La sérénité financière de chaque projet.',
},
      {
 key: 'booking', title: 'Réservation sans friction', claim: 'Contrats 360°.', desc: 'Devis et factures instantanés, conventions & états des lieux.',
},
      {
 key: 'scouting', title: 'Repérage', claim: 'Une réponse « off-market ».', desc: 'Un réseau de repéreurs accrédités permettant de répondre sous 72 h, même aux recherches les plus complexes.',
},
    ],
  },
  testimonials: {
    kicker: 'témoignages',
    title: "Ce qu'en disent les productions.",
    watch: 'Voir la vidéo',
    watchAria: 'voir le témoignage vidéo',
    featured: {
      quote: 'Avec la nouvelle fonctionnalité IA, on a eu beaucoup plus de facilité à trouver le lieu parfait pour notre scène.',
      name: 'Guillaume',
      role: 'Court-métrage — Moncher Production',
      initials: 'G',
    },
    side: [
      {
 quote: 'Process simplifié, on gagne un temps précieux.', name: 'Tiphaine', role: 'DA Image — My Little Box', initials: 'T',
},
      {
 quote: 'Les lieux reflètent la créativité de nos clips.', name: 'Erwan', role: 'Fondateur — Moonlife Records', initials: 'E',
},
    ],
  },
  host: {
    kicker: 'devenez hôte, estimez vos gains',
    perMonth: '€/mois',
    typeLabel: "Type d'espace",
    cityLabel: 'Ville',
    surfaceLabel: 'Surface',
    estimated: 'Tarif estimé',
    perDay: '€ / jour',
    daysPerMonth: 'j de tournage / mois',
    cta: 'Devenir hôte',
    videoLabel: "Témoignage d'hôte · 1 min",
    videoAria: "voir le témoignage d'hôte",
    posterAlt: 'Un hôte We Are Scene témoigne',
    types: {
      apartment: 'Appartement', house: 'Maison', loft: 'Loft', castle: 'Château', mansion: 'Hôtel Particulier', boat: 'Bateau', hotel: 'Hôtel', gallery: 'Galerie', restaurant: 'Restaurant - Bar - Café', studio: 'Studio Photo-Vidéo - Plateau', warehouse: 'Hangar - Entrepôt', outdoor: 'Extérieurs', monument: 'Monuments / lieux publics', kitchen: 'Cuisine Professionnelle', gym: 'Salle de sport', offices: 'Bureaux', workshop: 'Atelier - garage',
    },
    cities: {
      paris: 'Paris', idf: 'Île-de-France', lyon: 'Lyon', marseille: 'Marseille', bordeaux: 'Bordeaux', other: 'Autre',
    },
  },
  cta: {
    title: 'Votre prochain décor vous attend.',
    titleAccent: 'vous attend.',
    search: 'Rechercher un lieu',
    brief: 'Confier un brief',
  },
};

const en = {
  hero: {
    title: 'Book one-of-a-kind sets',
    titleAccent: 'one-of-a-kind sets',
    subtitle: 'Over 1 million scenes to book instantly, in the venues of thousands of hosts.',
    howItWorks: 'How does it work?',
    formats: ['Film', 'Music video', 'Social content', 'Documentary', 'Interview', 'Advertising', 'Photo shoot', 'TV & series'],
    search: {
      location: 'location',
      dates: 'dates',
      keywords: 'keywords · AI',
      keywordPlaceholders: ['Kitchen island, kids bedroom…', 'Glass roof, brick, north light…', 'Indoor pool, marble, 70s…', 'Rooftop, neon, open view…'],
      noOptions: 'Type a keyword…',
      button: 'Search',
      calendar: {
        datesTitle: 'Your dates',
        toHourly: 'Time slot',
        arrival: 'Arrival',
        departure: 'Departure',
        hoursShort: '{n} h',
        remove: 'Remove this date',
        flexibility: 'Flexibility',
        flexFixed: 'Fixed dates',
        flexDays: '± {n} d',
        clear: 'Clear',
        done: 'Done',
        more: '+{n}',
        period: {
          day: 'Full day', morning: 'Morning', afternoon: 'Afternoon', hour: 'By the hour',
        },
      },
    },
  },
  sceneLabel: 'Scene',
  proof: { count: '50,000+', label: 'productions' },
  selection: {
    kicker: 'pick of the week',
    title: 'The venues of thousands of hosts become the stage of your most captivating productions.',
    all: 'All venues',
    persons: 'people',
  },
  brief: {
    kicker: 'expert support',
    title: 'Tell us about your production, our team of experts takes it from there.',
    desc: 'A dedicated contact reviews your brief, guides your search and can put together a selection tailored to your project and budget.',
    cta: 'Send us your brief',
    imageAlt: 'The We Are Scene team supports you',
    offTitle: 'When the set is not online yet.',
    offDesc: 'Beyond the venues you can see, our network of accredited location scouts delivers a fast, relevant answer at no extra cost, even for the most complex searches.',
  },
  hof: {
    kicker: 'hall of fame',
    title: 'The more you shoot, the more you take centre stage.',
    desc: 'Your account loyalty is rewarded: bookings from all your teammates add up, and the perks benefit the whole team.',
    more: 'Learn more',
    tiers: {
      scout: { name: 'Scout', signature: 'Enter the scene', threshold: 'From 5 bookings / year' },
      blockbuster: { name: 'Blockbuster', signature: 'Centre stage', threshold: 'From 10 bookings / year' },
      legend: { name: 'Legend', signature: 'In the spotlight', threshold: 'From 30 bookings / year' },
    },
    plusPrefix: 'Legend +, ',
    plusUpTo: 'up to',
    plusPrice: '€49/month with commitment',
    plusSuffix: ': exclusive perks of up to -30%',
    plusCheck: 'Pays off from your very first bookings',
    plusFoot: 'Dedicated contact · member events · centralised follow-up',
  },
  usages: {
    kicker: 'your projects',
    titleLine1: 'Every audiovisual format.',
    titleLine2: 'One platform,',
    titleFor: 'for',
    words: ['your film.', 'your series.', 'your ad.', 'your music video.', 'your shoot.', 'your documentary.'],
    prev: 'previous',
    next: 'next',
    items: [
      { key: 'cinema', title: 'Film', claim: 'Premium scouting, online contracts.' },
      { key: 'clip', title: 'Music video', claim: 'Sets that live up to your universe.' },
      { key: 'contenu', title: 'Social content', claim: 'The set in the morning, the post at night.' },
      { key: 'docu', title: 'Documentary', claim: 'Real places for real stories.' },
      { key: 'interview', title: 'Interview', claim: 'A polished setting, ready to light.' },
      { key: 'pub', title: 'Advertising', claim: 'From brief to spot, no friction.' },
      { key: 'shooting', title: 'Photo shoot', claim: 'Sets ready to shoot.' },
      { key: 'tv', title: 'TV & series', claim: 'From the pilot to season 5.' },
    ],
  },
  pillars: {
    kicker: 'our services',
    titleLine1: 'From scouting to production,',
    titleLine2: 'everything is covered.',
    partner: 'in partnership with',
    items: [
      {
 key: 'ai', title: 'AI matching', claim: 'The right set, instantly.', desc: 'Image recognition across 1M+ photos. Type a few keywords, we surface the sets that match.',
},
      {
 key: 'cover', title: 'Scene Cover', claim: 'Coverage up to €1.5M.', desc: 'Financial peace of mind for every project.',
},
      {
 key: 'booking', title: 'Frictionless booking', claim: '360° contracts.', desc: 'Instant quotes and invoices, agreements & inventory reports.',
},
      {
 key: 'scouting', title: 'Location scouting', claim: 'An "off-market" answer.', desc: 'A network of accredited scouts able to answer within 72h, even for the most complex searches.',
},
    ],
  },
  testimonials: {
    kicker: 'testimonials',
    title: 'What productions say.',
    watch: 'Watch the video',
    watchAria: 'watch the video testimonial',
    featured: {
      quote: 'With the new AI feature, finding the perfect place for our scene became so much easier.',
      name: 'Guillaume',
      role: 'Short film — Moncher Production',
      initials: 'G',
    },
    side: [
      {
 quote: 'A simplified process, we save precious time.', name: 'Tiphaine', role: 'Image AD — My Little Box', initials: 'T',
},
      {
 quote: 'The venues reflect the creativity of our music videos.', name: 'Erwan', role: 'Founder — Moonlife Records', initials: 'E',
},
    ],
  },
  host: {
    kicker: 'become a host, estimate your earnings',
    perMonth: '€/month',
    typeLabel: 'Type of space',
    cityLabel: 'City',
    surfaceLabel: 'Surface',
    estimated: 'Estimated rate',
    perDay: '€ / day',
    daysPerMonth: 'shooting days / month',
    cta: 'Become a host',
    videoLabel: 'Host testimonial · 1 min',
    videoAria: 'watch the host testimonial',
    posterAlt: 'A We Are Scene host shares their experience',
    types: {
      apartment: 'Apartment', house: 'House', loft: 'Loft', castle: 'Castle', mansion: 'Mansion', boat: 'Boat', hotel: 'Hotel', gallery: 'Gallery', restaurant: 'Restaurant - Bar - Café', studio: 'Photo / video studio', warehouse: 'Warehouse', outdoor: 'Outdoors', monument: 'Monuments / public places', kitchen: 'Professional kitchen', gym: 'Gym', offices: 'Offices', workshop: 'Workshop - garage',
    },
    cities: {
      paris: 'Paris', idf: 'Paris region', lyon: 'Lyon', marseille: 'Marseille', bordeaux: 'Bordeaux', other: 'Other',
    },
  },
  cta: {
    title: 'Your next set is waiting.',
    titleAccent: 'is waiting.',
    search: 'Search a venue',
    brief: 'Send a brief',
  },
};

const es = {
  hero: {
    title: 'Reserva decorados únicos',
    titleAccent: 'decorados únicos',
    subtitle: 'Más de 1 millón de escenas para reservar al instante en los espacios de miles de anfitriones.',
    howItWorks: '¿Cómo funciona?',
    formats: ['Cine', 'Videoclip', 'Contenido social', 'Documental', 'Entrevista', 'Publicidad', 'Shooting', 'TV y series'],
    search: {
      location: 'localización',
      dates: 'fechas',
      keywords: 'palabras clave · IA',
      keywordPlaceholders: ['Isla de cocina, habitación infantil…', 'Cristalera, ladrillo, luz del norte…', 'Piscina interior, mármol, años 70…', 'Rooftop, neón, vistas despejadas…'],
      noOptions: 'Escribe una palabra clave…',
      button: 'Buscar',
      calendar: {
        datesTitle: 'Tus fechas',
        toHourly: 'Franja horaria',
        arrival: 'Llegada',
        departure: 'Salida',
        hoursShort: '{n} h',
        remove: 'Quitar esta fecha',
        flexibility: 'Flexibilidad',
        flexFixed: 'Fechas fijas',
        flexDays: '± {n} d',
        clear: 'Borrar',
        done: 'Listo',
        more: '+{n}',
        period: {
          day: 'Día completo', morning: 'Mañana', afternoon: 'Tarde', hour: 'Por hora',
        },
      },
    },
  },
  sceneLabel: 'Escena',
  proof: { count: '50.000+', label: 'producciones' },
  selection: {
    kicker: 'selección de la semana',
    title: 'Los espacios de miles de anfitriones se convierten en el escenario de tus producciones más cautivadoras.',
    all: 'Todos los espacios',
    persons: 'pers.',
  },
  brief: {
    kicker: 'acompañamiento experto',
    title: 'Cuéntanos tu producción, nuestro equipo de expertos te acompaña.',
    desc: 'Un interlocutor dedicado estudia tu brief, te guía en la búsqueda y puede proponerte una selección adaptada a tu proyecto y a tu presupuesto.',
    cta: 'Confíanos tu brief',
    imageAlt: 'El equipo de We Are Scene te acompaña',
    offTitle: 'Cuando el decorado todavía no existe en línea.',
    offDesc: 'Más allá de los espacios visibles, nuestra red de localizadores acreditados aporta una respuesta rápida y pertinente, sin coste adicional, incluso para las búsquedas más complejas.',
  },
  hof: {
    kicker: 'hall of fame',
    title: 'Cuanto más ruedas, más protagonismo tienes en escena.',
    desc: 'La fidelidad de tu cuenta se recompensa: las reservas de todos tus colaboradores se acumulan y las ventajas benefician a todo el equipo.',
    more: 'Saber más',
    tiers: {
      scout: { name: 'Scout', signature: 'Entra en escena', threshold: 'Desde 5 reservas / año' },
      blockbuster: { name: 'Blockbuster', signature: 'En primera fila', threshold: 'Desde 10 reservas / año' },
      legend: { name: 'Legend', signature: 'Bajo los focos', threshold: 'Desde 30 reservas / año' },
    },
    plusPrefix: 'Legend +, ',
    plusUpTo: 'hasta',
    plusPrice: '49 €/mes con compromiso',
    plusSuffix: ': ventajas exclusivas de hasta -30%',
    plusCheck: 'Rentable desde las primeras reservas',
    plusFoot: 'Interlocutor dedicado · eventos para miembros · seguimiento centralizado',
  },
  usages: {
    kicker: 'tus proyectos',
    titleLine1: 'Todos los formatos audiovisuales.',
    titleLine2: 'Una sola plataforma,',
    titleFor: 'para',
    words: ['tu película.', 'tu serie.', 'tu anuncio.', 'tu videoclip.', 'tu shooting.', 'tu documental.'],
    prev: 'anterior',
    next: 'siguiente',
    items: [
      { key: 'cinema', title: 'Cine', claim: 'Localización premium, contratos en línea.' },
      { key: 'clip', title: 'Videoclip', claim: 'Decorados a la altura de tus universos.' },
      { key: 'contenu', title: 'Contenido social', claim: 'El decorado por la mañana, el post por la noche.' },
      { key: 'docu', title: 'Documental', claim: 'Lugares reales para historias reales.' },
      { key: 'interview', title: 'Entrevista', claim: 'Un marco cuidado, listo para iluminar.' },
      { key: 'pub', title: 'Publicidad', claim: 'Del brief al spot, sin fricción.' },
      { key: 'shooting', title: 'Shooting', claim: 'Decorados listos para disparar.' },
      { key: 'tv', title: 'TV y series', claim: 'Del piloto a la temporada 5.' },
    ],
  },
  pillars: {
    kicker: 'nuestros servicios',
    titleLine1: 'De la búsqueda a la producción,',
    titleLine2: 'todo está cubierto.',
    partner: 'en colaboración con',
    items: [
      {
 key: 'ai', title: 'Matching IA', claim: 'El decorado adecuado, al instante.', desc: 'Reconocimiento de imagen sobre más de 1M de fotos. Escribe unas palabras clave y te mostramos los decorados que encajan.',
},
      {
 key: 'cover', title: 'Scene Cover', claim: 'Cobertura hasta 1,5 M€.', desc: 'La tranquilidad financiera de cada proyecto.',
},
      {
 key: 'booking', title: 'Reserva sin fricción', claim: 'Contratos 360°.', desc: 'Presupuestos y facturas al instante, convenios e inventarios.',
},
      {
 key: 'scouting', title: 'Localización', claim: 'Una respuesta «off-market».', desc: 'Una red de localizadores acreditados capaz de responder en 72 h, incluso a las búsquedas más complejas.',
},
    ],
  },
  testimonials: {
    kicker: 'testimonios',
    title: 'Lo que dicen las producciones.',
    watch: 'Ver el vídeo',
    watchAria: 'ver el testimonio en vídeo',
    featured: {
      quote: 'Con la nueva funcionalidad de IA, encontrar el lugar perfecto para nuestra escena fue mucho más fácil.',
      name: 'Guillaume',
      role: 'Cortometraje — Moncher Production',
      initials: 'G',
    },
    side: [
      {
 quote: 'Proceso simplificado, ganamos un tiempo precioso.', name: 'Tiphaine', role: 'DA Imagen — My Little Box', initials: 'T',
},
      {
 quote: 'Los espacios reflejan la creatividad de nuestros videoclips.', name: 'Erwan', role: 'Fundador — Moonlife Records', initials: 'E',
},
    ],
  },
  host: {
    kicker: 'conviértete en anfitrión, estima tus ingresos',
    perMonth: '€/mes',
    typeLabel: 'Tipo de espacio',
    cityLabel: 'Ciudad',
    surfaceLabel: 'Superficie',
    estimated: 'Tarifa estimada',
    perDay: '€ / día',
    daysPerMonth: 'días de rodaje / mes',
    cta: 'Ser anfitrión',
    videoLabel: 'Testimonio de anfitrión · 1 min',
    videoAria: 'ver el testimonio del anfitrión',
    posterAlt: 'Un anfitrión de We Are Scene comparte su experiencia',
    types: {
      apartment: 'Apartamento', house: 'Casa', loft: 'Loft', castle: 'Castillo', mansion: 'Palacete', boat: 'Barco', hotel: 'Hotel', gallery: 'Galería', restaurant: 'Restaurante - Bar - Café', studio: 'Estudio foto-vídeo - Plató', warehouse: 'Nave - Almacén', outdoor: 'Exteriores', monument: 'Monumentos / lugares públicos', kitchen: 'Cocina profesional', gym: 'Gimnasio', offices: 'Oficinas', workshop: 'Taller - garaje',
    },
    cities: {
      paris: 'París', idf: 'Île-de-France', lyon: 'Lyon', marseille: 'Marsella', bordeaux: 'Burdeos', other: 'Otra',
    },
  },
  cta: {
    title: 'Tu próximo decorado te espera.',
    titleAccent: 'te espera.',
    search: 'Buscar un espacio',
    brief: 'Enviar un brief',
  },
};

export const HOME_COPY = { fr, en, es };

export function useHomeCopy() {
  const { t, lang } = useTranslation();
  const dict = HOME_COPY[lang] || HOME_COPY.fr;

  // `home:v5.<path>` depuis l'API i18n si présent, sinon dictionnaire local.
  const tx = (path) => {
    const key = `home:v5.${path}`;
    const fromApi = t(key);
    if (fromApi && fromApi !== key) return fromApi;
    const local = getValue(dict, path);
    return local === undefined ? getValue(HOME_COPY.fr, path) : local;
  };

  return { tx, dict, lang };
}

export default useHomeCopy;
